import React, { useState } from "react";
import {
  View,
  Text,
  Pressable,
  ScrollView,
  Platform,
  Image,
  Linking,
  type ImageSourcePropType,
  type StyleProp,
  type TextStyle,
} from "react-native";
import {
  Check,
  X as XIcon,
  Wallet,
  ArrowUpRight,
  ChevronRight,
  CircleCheck,
  UserCheck,
  CircleX,
  FileText,
} from "lucide-react-native";
import ViewAllLink from "../ViewAllLink";
import ClinicInfoEmptyState from "./ClinicInfoEmptyState";
import {
  ProgramDetailsDialog,
  type ProgramDetailsFact,
} from "../access/ProgramDetailsDialog";
import { pricingSurfaceColors } from "./clinicPricingTokens";
import type {
  ProviderPricing,
  PricingPackage,
  FinancingOption,
} from "@louise/providers/providers.types";
import { EMBER } from "../../../onboarding/theme";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import {
  Dialog,
  DialogHeader,
  CardSurface,
  CardTitleText,
  CardSubtitleText,
  IconButton,
  MetaText,
  MetricValueText,
  SectionTitle,
} from "@louise/moon-ui";
import { semanticColors } from "@louise/theme/tokens";

const CARD_WIDTH = 264;
const FINANCING_CARD_WIDTH = 224;

const CARD_FOOTNOTE_STYLE: TextStyle = {
  fontSize: 10,
  fontStyle: "italic",
  color: EMBER.colors.textFaint,
  lineHeight: 13,
};

function formatPrice(
  minorUnits: number,
  currency: string,
  t: TranslationFunction,
): string {
  if (minorUnits === 0) return t("access.pricing_card.free");

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(minorUnits / 100);
}

function normalizeLocation(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function locationMatches(candidate: string, location: string): boolean {
  const normalizedCandidate = normalizeLocation(candidate);
  const locationParts = location
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .map((part) => {
      const stateAliases: Record<string, string> = {
        ca: "california",
        ny: "newyork",
        or: "oregon",
      };
      return stateAliases[part] ?? part;
    });

  return locationParts.some((part) => {
    const normalizedPart = normalizeLocation(part);
    return (
      normalizedPart === normalizedCandidate ||
      normalizedPart.includes(normalizedCandidate) ||
      normalizedCandidate.includes(normalizedPart)
    );
  });
}

function formatPackagePrice(
  pkg: PricingPackage,
  location: string | undefined,
  t: TranslationFunction,
): string {
  const locationPrice = location
    ? pkg.locationPrices?.find((entry) =>
        locationMatches(entry.location, location),
      )
    : undefined;
  const price = locationPrice?.price ?? pkg.price;
  const priceMax =
    locationPrice?.priceMax ?? (!locationPrice ? pkg.priceMax : undefined);

  return priceMax
    ? `${formatPrice(price, pkg.currency, t)} – ${formatPrice(
        priceMax,
        pkg.currency,
        t,
      )}`
    : formatPrice(price, pkg.currency, t);
}

function CardTitle({
  children,
  numberOfLines = 2,
  style,
}: {
  children: React.ReactNode;
  numberOfLines?: number;
  style?: StyleProp<TextStyle>;
}) {
  return (
    <CardTitleText numberOfLines={numberOfLines} style={style}>
      {children}
    </CardTitleText>
  );
}

function CardDescription({
  children,
  numberOfLines = 3,
  style,
}: {
  children: React.ReactNode;
  numberOfLines?: number;
  style?: StyleProp<TextStyle>;
}) {
  return (
    <CardSubtitleText
      numberOfLines={numberOfLines}
      style={[{ marginTop: 0 }, style]}
    >
      {children}
    </CardSubtitleText>
  );
}

function CardPrice({
  children,
  free = false,
}: {
  children: React.ReactNode;
  free?: boolean;
}) {
  return (
    <MetricValueText
      size="compact"
      style={{ color: free ? EMBER.colors.green : EMBER.colors.text }}
    >
      {children}
    </MetricValueText>
  );
}

function CardFootnote({ children }: { children: React.ReactNode }) {
  return (
    <Text
      className="font-dm-sans"
      numberOfLines={3}
      style={CARD_FOOTNOTE_STYLE}
    >
      {children}
    </Text>
  );
}

function HorizontalScroll({ children }: { children: React.ReactNode }) {
  return (
    <View
      style={
        {
          marginHorizontal: -20,
          ...(Platform.OS === "web" && {
            WebkitMaskImage:
              "linear-gradient(to right, black 90%, transparent 100%)",
            maskImage: "linear-gradient(to right, black 90%, transparent 100%)",
          }),
        } as Record<string, unknown>
      }
    >
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          gap: 12,
          paddingLeft: 20,
          paddingRight: 32,
        }}
      >
        {children}
      </ScrollView>
    </View>
  );
}

function PackageCard({
  pkg,
  location,
}: {
  pkg: PricingPackage;
  location?: string;
}) {
  const t = useTranslations();
  const priceLabel = formatPackagePrice(pkg, location, t);

  return (
    <CardSurface
      style={{
        width: CARD_WIDTH,
        padding: 14,
        minHeight: 224,
        gap: 8,
        justifyContent: "space-between",
      }}
    >
      <View style={{ gap: 2 }}>
        <CardTitle>{pkg.name}</CardTitle>

        {pkg.description ? (
          <CardDescription>{pkg.description}</CardDescription>
        ) : null}

        <CardPrice free={pkg.price === 0}>{priceLabel}</CardPrice>

        {pkg.includes && pkg.includes.length > 0 ? (
          <View style={{ gap: 3, marginTop: 2 }}>
            {pkg.includes.map((item, i) => (
              <View
                key={i}
                style={{
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 5,
                }}
              >
                <Check
                  size={10}
                  color={semanticColors.status.success}
                  strokeWidth={2.5}
                  style={{ marginTop: 2 }}
                />
                <MetaText
                  numberOfLines={2}
                  style={{ marginTop: 0, color: EMBER.colors.textMid, flex: 1 }}
                >
                  {item}
                </MetaText>
              </View>
            ))}
          </View>
        ) : null}

        {pkg.excludes && pkg.excludes.length > 0 ? (
          <View style={{ gap: 3, marginTop: 6 }}>
            {pkg.excludes.map((item, i) => (
              <View
                key={i}
                style={{
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 5,
                }}
              >
                <XIcon
                  size={10}
                  color={semanticColors.status.danger}
                  strokeWidth={2.5}
                  style={{ marginTop: 2 }}
                />
                <MetaText
                  numberOfLines={2}
                  style={{
                    marginTop: 0,
                    color: EMBER.colors.textFaint,
                    flex: 1,
                  }}
                >
                  {item}
                </MetaText>
              </View>
            ))}
          </View>
        ) : null}
      </View>

      {/* Footer note — pushed to bottom */}
      {pkg.notes ? <CardFootnote>{pkg.notes}</CardFootnote> : null}
    </CardSurface>
  );
}

// ── Pricing packages card ──

type PricingCardProps = {
  pricing: ProviderPricing;
  contactUrl?: string;
  location?: string;
};

function FullWidthPackageCard({
  pkg,
  location,
}: {
  pkg: PricingPackage;
  location?: string;
}) {
  const t = useTranslations();
  const priceLabel = formatPackagePrice(pkg, location, t);

  return (
    <CardSurface
      variant="muted"
      style={{
        padding: 16,
        gap: 4,
      }}
    >
      <CardTitle numberOfLines={3}>{pkg.name}</CardTitle>
      {pkg.description ? (
        <CardDescription>{pkg.description}</CardDescription>
      ) : null}
      <CardPrice free={pkg.price === 0}>{priceLabel}</CardPrice>

      {pkg.includes && pkg.includes.length > 0 ? (
        <View style={{ gap: 3, marginTop: 6 }}>
          {pkg.includes.map((item, i) => (
            <View
              key={i}
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 6,
              }}
            >
              <Check
                size={11}
                color={semanticColors.status.success}
                strokeWidth={2.5}
                style={{ marginTop: 2 }}
              />
              <MetaText
                style={{ marginTop: 0, color: EMBER.colors.textMid, flex: 1 }}
              >
                {item}
              </MetaText>
            </View>
          ))}
        </View>
      ) : null}

      {pkg.excludes && pkg.excludes.length > 0 ? (
        <View style={{ gap: 3, marginTop: 4 }}>
          {pkg.excludes.map((item, i) => (
            <View
              key={i}
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 6,
              }}
            >
              <XIcon
                size={11}
                color={semanticColors.status.danger}
                strokeWidth={2.5}
                style={{ marginTop: 2 }}
              />
              <MetaText
                style={{ marginTop: 0, color: EMBER.colors.textFaint, flex: 1 }}
              >
                {item}
              </MetaText>
            </View>
          ))}
        </View>
      ) : null}

      {pkg.notes ? <CardFootnote>{pkg.notes}</CardFootnote> : null}
    </CardSurface>
  );
}

export default function ClinicPricingCard({
  pricing,
  contactUrl,
  location,
}: PricingCardProps) {
  const t = useTranslations();
  const [modalVisible, setModalVisible] = useState(false);
  const hasPackages = pricing.packages && pricing.packages.length > 0;
  const sectionHeader = (
    <View
      style={{
        marginTop: 20,
        marginBottom: 10,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <SectionTitle icon={Wallet}>
        {t("access.clinic_detail.section.pricing")}
      </SectionTitle>
      {hasPackages && pricing.packages.length > 1 ? (
        <ViewAllLink onPress={() => setModalVisible(true)} />
      ) : null}
    </View>
  );

  // Always render the pricing card, even with no data, so the cost section is
  // never silently missing. When we have nothing, say so plainly.
  if (!hasPackages) {
    return (
      <>
        {sectionHeader}
        <ClinicInfoEmptyState
          message={t("access.clinic_detail.empty.pricing")}
          contactUrl={contactUrl}
        />
      </>
    );
  }

  return (
    <>
      {sectionHeader}
      <View style={{ gap: 12 }}>
        <HorizontalScroll>
          {pricing.packages.map((pkg, i) => (
            <PackageCard key={i} pkg={pkg} location={location} />
          ))}
        </HorizontalScroll>

        {pricing.disclaimer ? (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 10,
              color: EMBER.colors.textFaint,
              lineHeight: 14,
            }}
          >
            {pricing.disclaimer}
          </Text>
        ) : null}
      </View>

      <Dialog
        size="wide"
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        surface="solid"
        closeButton="minimal"
        closeAccessibilityLabel={t("learn.article_actions.close_accessibility")}
        closeAction="close_pricing_modal"
        contentStyle={{
          paddingTop: 20,
          paddingHorizontal: 20,
          paddingBottom: 20,
        }}
      >
        <DialogHeader
          title={t("access.clinic_detail.section.treatment_pricing")}
          style={{ marginBottom: 16 }}
        />

        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ gap: 10, paddingBottom: 4 }}
        >
          {pricing.packages.map((pkg, i) => (
            <FullWidthPackageCard key={i} pkg={pkg} location={location} />
          ))}

          {pricing.disclaimer ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 10,
                color: EMBER.colors.textFaint,
                lineHeight: 14,
                marginTop: 4,
              }}
            >
              {pricing.disclaimer}
            </Text>
          ) : null}
        </ScrollView>
      </Dialog>
    </>
  );
}

// ── Financing options card ──

type FinancingEnrichment = {
  logo: ImageSourcePropType;
  taglineKey: string;
  highlightKeys: string[];
  url: string;
};

const FINANCING_ENRICHMENTS: Record<string, FinancingEnrichment> = {
  patientfi: {
    logo: require("../../../assets/images/insurance/patientfi.png") as ImageSourcePropType,
    taglineKey: "access.pricing_card.enrichment.patientfi.tagline",
    highlightKeys: [
      "access.pricing_card.enrichment.patientfi.highlight_1",
      "access.pricing_card.enrichment.patientfi.highlight_2",
      "access.pricing_card.enrichment.patientfi.highlight_3",
    ],
    url: "https://www.patientfi.com",
  },
  capexmd: {
    logo: require("../../../assets/images/insurance/capexmd.png") as ImageSourcePropType,
    taglineKey: "access.pricing_card.enrichment.capexmd.tagline",
    highlightKeys: [
      "access.pricing_card.enrichment.capexmd.highlight_1",
      "access.pricing_card.enrichment.capexmd.highlight_2",
      "access.pricing_card.enrichment.capexmd.highlight_3",
    ],
    url: "https://www.capexmd.com",
  },
  "future family": {
    logo: require("../../../assets/images/insurance/futurefamily.png") as ImageSourcePropType,
    taglineKey: "access.pricing_card.enrichment.future_family.tagline",
    highlightKeys: [
      "access.pricing_card.enrichment.future_family.highlight_1",
      "access.pricing_card.enrichment.future_family.highlight_2",
      "access.pricing_card.enrichment.future_family.highlight_3",
    ],
    url: "https://www.futurefamily.com",
  },
  prosper: {
    logo: require("../../../assets/images/insurance/prosper.png") as ImageSourcePropType,
    taglineKey: "access.pricing_card.enrichment.prosper.tagline",
    highlightKeys: [
      "access.pricing_card.enrichment.prosper.highlight_1",
      "access.pricing_card.enrichment.prosper.highlight_2",
      "access.pricing_card.enrichment.prosper.highlight_3",
    ],
    url: "https://www.prosper.com",
  },
  lendingclub: {
    logo: require("../../../assets/images/insurance/lending-club.png") as ImageSourcePropType,
    taglineKey: "access.pricing_card.enrichment.lendingclub.tagline",
    highlightKeys: [
      "access.pricing_card.enrichment.lendingclub.highlight_1",
      "access.pricing_card.enrichment.lendingclub.highlight_2",
      "access.pricing_card.enrichment.lendingclub.highlight_3",
    ],
    url: "https://www.lendingclub.com",
  },
};

function matchEnrichment(name: string): FinancingEnrichment | null {
  const lower = name.toLowerCase();
  for (const [key, enrichment] of Object.entries(FINANCING_ENRICHMENTS)) {
    if (lower.includes(key)) return enrichment;
  }
  return null;
}

type FinancingCardProps = {
  options: FinancingOption[];
  contactUrl?: string;
  /** The clinic offering the options, named in each option's details. */
  clinicName?: string;
};

// An option opens its details when it says more than its card shows.
function hasFinancingDetails(option: FinancingOption): boolean {
  return Boolean(
    option.eligibility ||
    option.exclusions ||
    option.notes ||
    option._source?.url,
  );
}

function EnrichedFinancingCard({
  option,
  enrichment,
}: {
  option: FinancingOption;
  enrichment: FinancingEnrichment;
}) {
  const t = useTranslations();
  return (
    <View
      style={{
        width: FINANCING_CARD_WIDTH,
        borderRadius: 16,
        backgroundColor: pricingSurfaceColors.cardBackground,
        borderWidth: 1,
        borderColor: pricingSurfaceColors.cardBorder,
        padding: 14,
        gap: 4,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <Image
          source={enrichment.logo}
          style={{ width: 24, height: 24, borderRadius: 6 }}
          resizeMode="contain"
        />
        <CardTitle numberOfLines={2} style={{ flex: 1 }}>
          {option.name}
        </CardTitle>
        <IconButton
          accessibilityLabel={t("access.pricing_card.learn_more")}
          action="access.pricing_card.learn_more"
          onPress={() => void Linking.openURL(enrichment.url)}
          hitSlop={8}
          size={30}
          surface="frosted"
        >
          <ArrowUpRight size={15} color={EMBER.colors.text} strokeWidth={1.8} />
        </IconButton>
      </View>

      <CardDescription numberOfLines={2}>
        {t(enrichment.taglineKey)}
      </CardDescription>

      <View style={{ gap: 3 }}>
        {enrichment.highlightKeys.map((item, i) => (
          <View
            key={i}
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 5,
            }}
          >
            <Check
              size={10}
              color={semanticColors.status.success}
              strokeWidth={2.5}
              style={{ marginTop: 2 }}
            />
            <MetaText
              numberOfLines={2}
              style={{ marginTop: 0, color: EMBER.colors.textMid, flex: 1 }}
            >
              {t(item)}
            </MetaText>
          </View>
        ))}
      </View>
    </View>
  );
}

function PlainFinancingCard({
  option,
  onPress,
}: {
  option: FinancingOption;
  onPress?: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("PlainFinancingCard");
  const description =
    option.description && option.description !== option.name
      ? option.description
      : t("access.pricing_card.financing_details_unavailable");
  const cardStyle = {
    width: FINANCING_CARD_WIDTH,
    borderRadius: 16,
    backgroundColor: pricingSurfaceColors.cardBackground,
    borderWidth: 1,
    borderColor: pricingSurfaceColors.cardBorder,
    padding: 14,
    gap: 4,
  };
  const content = (
    <>
      {onPress ? (
        <View
          style={{ flexDirection: "row", alignItems: "flex-start", gap: 8 }}
        >
          <CardTitle style={{ flex: 1 }}>{option.name}</CardTitle>
          <ChevronRight
            size={17}
            color={EMBER.colors.textFaint}
            strokeWidth={1.6}
            style={{ marginTop: 1 }}
          />
        </View>
      ) : (
        <CardTitle>{option.name}</CardTitle>
      )}

      {description ? <CardDescription>{description}</CardDescription> : null}

      {option.details && option.details.length > 0 ? (
        <View style={{ gap: 3 }}>
          {option.details.map((item, i) => (
            <View
              key={i}
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 5,
              }}
            >
              <Check
                size={10}
                color={semanticColors.status.success}
                strokeWidth={2.5}
                style={{ marginTop: 2 }}
              />
              <MetaText
                numberOfLines={2}
                style={{ marginTop: 0, color: EMBER.colors.textMid, flex: 1 }}
              >
                {item}
              </MetaText>
            </View>
          ))}
        </View>
      ) : null}
    </>
  );

  if (!onPress) return <View style={cardStyle}>{content}</View>;

  return (
    // A plain Pressable so the card still stretches to the row's height.
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${option.name}. ${t("access.pricing_card.view_financing_details")}`}
      onPress={() => {
        capturePress(
          pressLabel(option.name),
          "access.pricing_card.view_financing_details",
        );
        onPress();
      }}
      style={({ pressed }) => [cardStyle, pressed ? { opacity: 0.8 } : null]}
    >
      {content}
    </Pressable>
  );
}

function FinancingOptionDialog({
  option,
  clinicName,
  onClose,
}: {
  option: FinancingOption;
  clinicName?: string;
  onClose: () => void;
}) {
  const t = useTranslations();
  const facts: ProgramDetailsFact[] = [];
  if (option.details && option.details.length > 0) {
    facts.push({
      label: t("access.pricing_card.financing_whats_included"),
      icon: CircleCheck,
      value: option.details.join("\n"),
    });
  }
  if (option.eligibility) {
    facts.push({
      label: t("access.pricing_card.financing_who_qualifies"),
      icon: UserCheck,
      value: option.eligibility,
    });
  }
  if (option.exclusions) {
    facts.push({
      label: t("access.pricing_card.financing_not_included"),
      icon: CircleX,
      value: option.exclusions,
    });
  }
  if (option.notes) {
    facts.push({
      label: t("access.pricing_card.financing_fine_print"),
      icon: FileText,
      value: option.notes,
    });
  }
  const summary =
    option.description && option.description !== option.name
      ? option.description
      : undefined;

  return (
    <ProgramDetailsDialog
      title={option.name}
      subtitle={clinicName}
      icon={Wallet}
      summary={summary}
      facts={facts}
      source={
        option._source?.url
          ? { url: option._source.url, checkedOn: option._source.fetchedAt }
          : undefined
      }
      onClose={onClose}
      analytics={{
        close: "access.pricing_card.close_financing_details",
        backdrop: "dismiss_financing_details",
        openSource: "access.pricing_card.open_financing_source",
      }}
      testID="financing-option-dialog"
    />
  );
}

export function ClinicFinancingCard({
  options,
  contactUrl,
  clinicName,
}: FinancingCardProps) {
  const t = useTranslations();
  const [openOption, setOpenOption] = useState<FinancingOption | null>(null);
  if (!options || options.length === 0) {
    return (
      <ClinicInfoEmptyState
        message={t("access.pricing_card.financing_empty")}
        contactUrl={contactUrl}
      />
    );
  }

  return (
    <>
      <HorizontalScroll>
        {options.map((option, i) => {
          const enrichment = matchEnrichment(option.name);
          return enrichment ? (
            <EnrichedFinancingCard
              key={i}
              option={option}
              enrichment={enrichment}
            />
          ) : (
            <PlainFinancingCard
              key={i}
              option={option}
              onPress={
                hasFinancingDetails(option)
                  ? () => setOpenOption(option)
                  : undefined
              }
            />
          );
        })}
      </HorizontalScroll>
      {openOption ? (
        <FinancingOptionDialog
          option={openOption}
          clinicName={clinicName}
          onClose={() => setOpenOption(null)}
        />
      ) : null}
    </>
  );
}
